import { useState, useEffect, useRef } from "react";

// ── PALETTES ───────────────────────────────────────────────────────────────
const DARK = {
  bg:       "#1a1410",
  surface:  "#221c16",
  card:     "#2a221a",
  border:   "#3d3028",
  brown:    "#6B4423",
  caramel:  "#C47B2B",
  beige:    "#D4B483",
  cream:    "#F2E8D5",
  white:    "#FAF6EF",
  muted:    "#8a7060",
  faint:    "#2f2520",
  error:    "#c0603a",
};

const LIGHT = {
  bg:      "#FAF6EF",
  surface: "#F2EAD8",
  card:    "#EDE0C8",
  border:  "#D4B896",
  brown:   "#6B4423",
  caramel: "#A0601A",
  beige:   "#5a3a18",
  cream:   "#2a1a08",
  white:   "#180e04",
  muted:   "#8a6840",
  faint:   "#E8D8BC",
  error:   "#c0603a",
};

// ── LANGUAGES ──────────────────────────────────────────────────────────────
const LANGS = [
  { code:"en", label:"English",   flag:"🇬🇧" },
  { code:"de", label:"Deutsch",   flag:"🇩🇪" },
  { code:"zh", label:"中文",       flag:"🇨🇳" },
  { code:"hi", label:"हिन्दी",     flag:"🇮🇳" },
  { code:"es", label:"Español",   flag:"🇪🇸" },
  { code:"fr", label:"Français",  flag:"🇫🇷" },
  { code:"ar", label:"العربية",   flag:"🇸🇦" },
  { code:"pt", label:"Português", flag:"🇧🇷" },
  { code:"ru", label:"Русский",   flag:"🇷🇺" },
  { code:"ja", label:"日本語",     flag:"🇯🇵" },
  { code:"bn", label:"বাংলা",     flag:"🇧🇩" },
  { code:"id", label:"Indonesia", flag:"🇮🇩" },
];

const CEFR = ["A1","A2","B1","B2","C1","C2"];
const ARTICLE_C = { der:"#C47B2B", die:"#D4B483", das:"#a0856a" };

const SERVICES = [
  { id:"translate", label:"Translate",      icon:"⇄", desc:"Word & phrase translation" },
  { id:"wod",       label:"Word of the Day",icon:"✦", desc:"Daily vocabulary" },
  { id:"flash",     label:"Flashcards",     icon:"▣", desc:"Sentence practice" },
  { id:"practice",  label:"Practice",       icon:"✎", desc:"Write & get corrected" },
];

// ── CLAUDE API ─────────────────────────────────────────────────────────────
async function claude(prompt) {
  const res = await fetch("/.netlify/functions/claude", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt }),
  });
  if (!res.ok) throw new Error(`API ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  const text = data.text || "";
  const match = text.replace(/```json\n?/g,"").replace(/```/g,"").trim().match(/\{[\s\S]*\}/);
  if (!match) throw new Error("No JSON");
  return JSON.parse(match[0]);
}

// ── HELPERS ────────────────────────────────────────────────────────────────
const getLang = code => LANGS.find(l => l.code === code) || LANGS[0];
const lname   = code => getLang(code).label;

// ── GLOBAL CSS ─────────────────────────────────────────────────────────────
function makeCSS(t) { return `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{background:${t.bg};overscroll-behavior:none;}
::-webkit-scrollbar{width:3px;height:3px;}
::-webkit-scrollbar-track{background:${t.bg};}
::-webkit-scrollbar-thumb{background:${t.brown};border-radius:2px;}
input,textarea,select,button{font-family:inherit;}
input:focus,textarea:focus,select:focus{outline:none;}
button{cursor:pointer;border:none;background:none;}

.app{
  min-height:100vh;
  background:${t.bg};
  font-family:'Inter',sans-serif;
  color:${t.cream};
  display:flex;
  flex-direction:column;
  align-items:center;
  padding:0 0 80px;
  position:relative;
  overflow-x:hidden;
  transition:background .3s ease;
}

/* ── HEADER ── */
.hdr{
  width:100%;max-width:620px;
  padding:36px 24px 0;
  display:flex;align-items:flex-start;justify-content:space-between;
  gap:12px;
}
.hdr-text{display:flex;flex-direction:column;align-items:flex-start;gap:4px;}
.logo{
  font-family:'Cormorant Garamond',serif;
  font-size:46px;font-weight:700;
  letter-spacing:-1px;
  color:${t.white};
  line-height:1;
}
.logo em{font-style:normal;color:${t.caramel};}
.tagline{font-size:10px;letter-spacing:3.5px;color:${t.muted};text-transform:uppercase;margin-top:2px;}

/* ── THEME SWITCH ── */
.theme-switch{
  position:relative;
  width:52px;height:28px;
  border-radius:16px;
  background:${t.faint};
  border:1px solid ${t.border};
  flex-shrink:0;
  margin-top:6px;
  transition:background .3s,border-color .3s;
}
.switch-knob{
  position:absolute;top:2px;left:2px;
  width:22px;height:22px;border-radius:50%;
  background:${t.caramel};
  display:flex;align-items:center;justify-content:center;
  box-shadow:0 1px 4px #00000033;
  transition:transform .32s cubic-bezier(.4,0,.2,1),background .3s;
}
.switch-knob.dark{transform:translateX(24px);background:${t.brown};}

/* ── SERVICE SCROLL ── */
.service-scroll-wrap{
  width:100%;max-width:620px;
  padding:24px 24px 0;
}
.service-scroll{
  display:flex;gap:10px;
  overflow-x:auto;
  padding-bottom:4px;
  scrollbar-width:none;
}
.service-scroll::-webkit-scrollbar{display:none;}
.svc-card{
  flex-shrink:0;
  width:128px;
  background:${t.surface};
  border:1px solid ${t.border};
  border-radius:16px;
  padding:18px 14px;
  display:flex;flex-direction:column;gap:6px;
  transition:all .25s;
  cursor:pointer;
  position:relative;
  overflow:hidden;
}
.svc-card::before{
  content:'';position:absolute;inset:0;
  background:linear-gradient(135deg,${t.brown}00,${t.brown}18);
  opacity:0;transition:opacity .25s;
}
.svc-card:hover::before,.svc-card.active::before{opacity:1;}
.svc-card.active{border-color:${t.caramel}88;background:${t.card};}
.svc-icon{font-size:22px;line-height:1;}
.svc-label{font-size:13px;font-weight:600;color:${t.cream};}
.svc-desc{font-size:10px;color:${t.muted};line-height:1.4;}
.svc-dot{
  width:5px;height:5px;border-radius:50%;
  background:${t.caramel};
  position:absolute;top:12px;right:12px;
  opacity:0;transition:opacity .2s;
}
.svc-card.active .svc-dot{opacity:1;}

/* ── PANEL ── */
.panel-wrap{
  width:100%;max-width:620px;
  padding:24px 24px 0;
}
.panel{
  animation:panelIn .35s cubic-bezier(.4,0,.2,1);
}
@keyframes panelIn{from{opacity:0;transform:translateY(18px)}to{opacity:1;transform:translateY(0)}}

/* ── SERVICE BAR (per-panel controls) ── */
.service-bar{
  display:flex;align-items:center;gap:8px;
  margin-bottom:16px;
}
.lang-sel{
  flex:1;
  background:${t.surface};
  border:1px solid ${t.border};
  border-radius:10px;
  color:${t.cream};
  font-size:13px;
  padding:10px 12px;
  appearance:none;
  text-align:center;
  cursor:pointer;
  transition:border-color .2s;
}
.lang-sel:hover,.lang-sel:focus{border-color:${t.caramel}66;}
.swap{
  width:36px;height:36px;border-radius:50%;
  background:${t.faint};border:1px solid ${t.border};
  color:${t.caramel};font-size:15px;
  display:flex;align-items:center;justify-content:center;
  flex-shrink:0;transition:all .2s;
}
.swap:hover{background:${t.brown}33;border-color:${t.brown};}

/* CEFR DROPDOWN */
.cefr-wrap{position:relative;flex-shrink:0;}
.cefr-trigger{
  display:flex;align-items:center;gap:8px;
  background:${t.surface};border:1px solid ${t.border};
  border-radius:10px;padding:10px 14px;
  color:${t.cream};font-size:12px;font-weight:500;
  transition:all .2s;
  white-space:nowrap;
}
.cefr-trigger:hover{border-color:${t.caramel}66;}
.cefr-badge{
  background:${t.brown};color:${t.cream};
  font-size:11px;font-weight:600;
  padding:2px 8px;border-radius:5px;letter-spacing:.5px;
}
.cefr-arrow{font-size:10px;color:${t.muted};transition:transform .25s;}
.cefr-arrow.open{transform:rotate(180deg);}
.cefr-dropdown{
  position:absolute;top:calc(100% + 6px);
  background:${t.card};border:1px solid ${t.border};
  border-radius:12px;padding:6px;
  display:flex;flex-direction:column;gap:2px;
  z-index:100;min-width:140px;
  box-shadow:0 8px 32px #00000033;
  animation:dropDown .2s ease;
}
.cefr-dropdown.right{right:0;}
.cefr-dropdown.left{left:0;}
@keyframes dropDown{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
.cefr-opt{
  padding:8px 14px;border-radius:8px;
  font-size:12px;font-weight:500;color:${t.muted};
  transition:all .15s;text-align:left;
  display:flex;justify-content:space-between;gap:10px;
}
.cefr-opt:hover{background:${t.faint};color:${t.cream};}
.cefr-opt.sel{background:${t.brown}33;color:${t.caramel};}

/* ── SHARED ── */
.section-label{
  font-size:10px;letter-spacing:2.5px;text-transform:uppercase;
  color:${t.muted};margin-bottom:10px;
}
.row-between{
  display:flex;align-items:center;justify-content:space-between;
  margin-bottom:10px;gap:10px;
}
.row-between .section-label{margin-bottom:0;}
.card{
  background:${t.surface};border:1px solid ${t.border};
  border-radius:16px;padding:20px;margin-bottom:12px;
  transition:border-color .2s,background .3s;
}
.card:hover{border-color:${t.brown}55;}
hr.div{border:none;border-top:1px solid ${t.faint};margin:16px 0;}

.main-input{
  width:100%;
  background:${t.faint};border:1px solid ${t.border};border-radius:10px;
  color:${t.cream};font-size:15px;padding:13px 16px;
  transition:border-color .2s;margin-bottom:10px;
}
.main-input:focus{border-color:${t.brown};}
.main-input::placeholder{color:${t.muted};}
.go-btn{
  width:100%;
  background:${t.brown};border:none;border-radius:10px;
  padding:13px;color:${t.cream};font-size:12px;font-weight:600;
  letter-spacing:1px;text-transform:uppercase;
  transition:all .15s;
}
.go-btn:hover:not(:disabled){background:#7d5030;transform:translateY(-1px);box-shadow:0 4px 16px ${t.brown}55;}
.go-btn:active:not(:disabled){transform:translateY(0);}
.go-btn:disabled{opacity:.4;cursor:not-allowed;}

.result-word{
  font-family:'Cormorant Garamond',serif;
  font-size:34px;font-weight:700;color:${t.white};
  line-height:1.15;margin-bottom:3px;
}
.result-meta{font-size:11px;color:${t.muted};letter-spacing:1.5px;text-transform:uppercase;margin-bottom:14px;}
.ex-sentence{font-size:14px;color:${t.cream};line-height:1.75;font-style:italic;margin-bottom:3px;}
.ex-trans{font-size:13px;color:${t.caramel};line-height:1.7;}

/* ARTICLE */
.article-row{display:flex;gap:7px;margin-bottom:14px;}
.art-pill{
  flex:1;text-align:center;padding:8px 4px;border-radius:8px;
  font-size:12px;font-weight:600;border:1px solid;transition:all .15s;
}

/* BREAKDOWN */
.bd-toggle{
  width:100%;
  background:${t.faint};border:1px solid ${t.border};border-radius:8px;
  padding:9px 14px;color:${t.muted};font-size:11px;letter-spacing:1.5px;
  text-align:left;transition:all .15s;margin-top:10px;
  display:flex;justify-content:space-between;align-items:center;
}
.bd-toggle:hover{border-color:${t.brown}66;color:${t.beige};}
.bd-list{margin-top:10px;display:flex;flex-direction:column;}
.bd-item{display:flex;gap:12px;padding:9px 0;border-bottom:1px solid ${t.faint};align-items:flex-start;}
.bd-item:last-child{border-bottom:none;}
.bd-word{font-size:13px;font-weight:600;color:${t.beige};min-width:85px;flex-shrink:0;}
.bd-role{font-size:12px;color:${t.muted};line-height:1.55;}

/* FLIP CARD */
.flip-outer{perspective:1200px;height:185px;cursor:pointer;margin-bottom:12px;}
.flip-inner{position:relative;width:100%;height:100%;transform-style:preserve-3d;transition:transform .55s cubic-bezier(.4,0,.2,1);}
.flip-inner.flipped{transform:rotateY(180deg);}
.flip-face{
  backface-visibility:hidden;-webkit-backface-visibility:hidden;
  position:absolute;inset:0;display:flex;flex-direction:column;
  align-items:center;justify-content:center;padding:22px;
  border-radius:16px;background:${t.surface};border:1px solid ${t.border};
}
.flip-back{transform:rotateY(180deg);border-color:${t.brown}66!important;}
.flip-hint{font-size:10px;color:${t.muted};letter-spacing:2px;text-transform:uppercase;position:absolute;bottom:13px;}

/* PRACTICE */
.notes-ta{
  width:100%;background:${t.faint};border:1px solid ${t.border};border-radius:10px;
  color:${t.cream};font-size:14px;line-height:1.7;padding:14px 16px;
  resize:vertical;min-height:110px;transition:border-color .2s;
  margin-bottom:10px;
}
.notes-ta:focus{border-color:${t.brown};}
.notes-ta::placeholder{color:${t.muted};}
.mistake-block{
  background:${t.faint};border-left:3px solid ${t.caramel};
  border-radius:0 8px 8px 0;padding:10px 14px;margin-bottom:8px;
}
.m-orig{font-size:13px;color:#c08070;text-decoration:line-through;margin-bottom:2px;}
.m-fix{font-size:13px;color:${t.cream};margin-bottom:4px;}
.m-exp{font-size:12px;color:${t.muted};line-height:1.5;}
.perfect-badge{
  text-align:center;padding:20px 0;
}
.perfect-check{font-size:32px;margin-bottom:6px;}
.perfect-text{font-size:15px;font-weight:600;color:${t.beige};}

/* HISTORY */
.history-item{
  background:${t.surface};border:1px solid ${t.border};border-radius:14px;
  padding:16px 18px;margin-bottom:10px;cursor:pointer;transition:all .2s;
}
.history-item:hover{border-color:${t.brown}66;}
.h-meta{font-size:10px;color:${t.muted};letter-spacing:1px;display:flex;justify-content:space-between;margin-bottom:7px;gap:10px;}
.h-orig{font-size:12px;color:${t.muted};margin-bottom:4px;font-style:italic;}
.h-corrected{font-size:14px;color:${t.cream};font-weight:500;line-height:1.5;}
.h-expand{overflow:hidden;transition:max-height .35s ease;max-height:0;}
.h-expand.open{max-height:1000px;}

/* WOD */
.wod-word{
  font-family:'Cormorant Garamond',serif;
  font-size:42px;font-weight:700;color:${t.white};
  line-height:1.1;margin-bottom:3px;
}
.cultural{
  font-size:13px;color:${t.muted};font-style:italic;line-height:1.65;
  border-left:2px solid ${t.brown};padding-left:12px;margin:12px 0;
}

/* LOADING */
.loading{display:flex;gap:6px;align-items:center;justify-content:center;padding:44px 0;}
.dot{width:7px;height:7px;border-radius:50%;background:${t.brown};animation:blink 1.2s infinite;}
.dot:nth-child(2){animation-delay:.2s;}.dot:nth-child(3){animation-delay:.4s;}
@keyframes blink{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.2;transform:scale(.65)}}

.error-msg{font-size:13px;color:${t.error};margin-bottom:12px;}

/* SUPPORT */
.support{
  display:flex;align-items:center;justify-content:center;gap:8px;
  width:calc(100% - 48px);max-width:572px;
  background:${t.surface};border:1px solid ${t.border};border-radius:12px;
  padding:12px 20px;color:${t.caramel};font-size:12px;font-weight:500;
  letter-spacing:.5px;text-decoration:none;margin-top:32px;transition:all .2s;
}
.support:hover{background:${t.brown}22;border-color:${t.brown};color:${t.beige};}
`;
}

// ── SHARED COMPONENTS ──────────────────────────────────────────────────────
function Loading() {
  return <div className="loading"><div className="dot"/><div className="dot"/><div className="dot"/></div>;
}

function ArticlePills({ active, t }) {
  if (!active || active === "none") return null;
  return (
    <div className="article-row">
      {["der","die","das"].map(a => {
        const color = ARTICLE_C[a];
        const on = a === active;
        return (
          <div key={a} className="art-pill" style={{
            background: on ? color+"22" : t.faint,
            borderColor: on ? color : t.border,
            color: on ? color : t.muted,
            fontWeight: on ? 700 : 400,
          }}>
            <div>{a}</div>
            <div style={{fontSize:9,marginTop:1,opacity:.7}}>{a==="der"?"masc.":a==="die"?"fem.":"neut."}</div>
          </div>
        );
      })}
    </div>
  );
}

function Breakdown({ items, label }) {
  const [open, setOpen] = useState(false);
  if (!items?.length) return null;
  return (
    <>
      <button className="bd-toggle" onClick={() => setOpen(o => !o)}>
        <span>{label || "Grammar Breakdown"}</span>
        <span style={{fontSize:9,transition:"transform .2s",transform:open?"rotate(180deg)":"none"}}>▼</span>
      </button>
      {open && (
        <div className="bd-list">
          {items.map((b,i) => (
            <div key={i} className="bd-item">
              <span className="bd-word">{b.word}</span>
              <span className="bd-role">{b.role}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function CefrDropdown({ cefr, setCefr, t, align = "right" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="cefr-wrap" ref={ref}>
      <button className="cefr-trigger" onClick={() => setOpen(o => !o)}>
        <span style={{color:t.muted,fontSize:11,letterSpacing:1}}>LEVEL</span>
        <span className="cefr-badge">{cefr}</span>
        <span className={`cefr-arrow${open?" open":""}`}>▼</span>
      </button>
      {open && (
        <div className={`cefr-dropdown ${align}`}>
          {CEFR.map(l => (
            <button key={l} className={`cefr-opt${cefr===l?" sel":""}`}
              onClick={() => { setCefr(l); setOpen(false); }}>
              <span>{l}</span>
              <span style={{fontSize:10,color:t.muted}}>
                {l==="A1"?"Beginner":l==="A2"?"Elementary":l==="B1"?"Intermediate":l==="B2"?"Upper-Int":l==="C1"?"Advanced":"Mastery"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── API CALLS ──────────────────────────────────────────────────────────────
async function apiTranslate(word, from, to, cefr) {
  return claude(`You are a professional linguist.
Translate "${word}" from ${lname(from)} to ${lname(to)}.
Write a CEFR ${cefr} example sentence in ${lname(to)} using the translated word, following correct grammar and syntax of ${lname(to)}.
Provide a grammatical breakdown of every word in that ${lname(to)} sentence.
${(to==="de"||from==="de")?`If the ${lname(to)} translation is a noun, specify the article (der/die/das).`:""}

JSON only, no markdown:
{"translation":"<${lname(to)} word>","article":"<der|die|das|none>","type":"<noun|verb|adj|adv|phrase|other>","example_to":"<CEFR ${cefr} sentence in ${lname(to)}>","example_from":"<that sentence in ${lname(from)}>","breakdown":[{"word":"<${lname(to)} word>","role":"<grammar explanation>"}]}`);
}

async function apiWod(lang, cefr) {
  return claude(`Generate a culturally interesting "Word of the Day" in ${lname(lang)}.
${lang==="de"?"If it is a noun, include its article (der/die/das).":""}
Provide a short, clear English definition of the word's meaning.
Write a CEFR ${cefr} example sentence in ${lname(lang)} using the word, following correct grammar and syntax of ${lname(lang)}.
Provide a full grammatical breakdown of every word in that example sentence.

JSON only, no markdown:
{"word":"<word in ${lname(lang)}>","meaning":"<short English definition>","article":"<der|die|das|none>","type":"<noun|verb|adj|adv|phrase|other>","cultural_note":"<one sentence cultural or usage note, in English>","example":"<CEFR ${cefr} sentence in ${lname(lang)}>","breakdown":[{"word":"<word from the example>","role":"<grammar explanation>"}]}`);
}

async function apiFlash(lang, cefr) {
  return claude(`Generate a unique CEFR ${cefr} sentence in ${lname(lang)}, grammatically correct and natural.
Provide an English translation${lang==="en"?" (a natural rephrasing of the sentence, since it is already in English)":""}.
Provide a full grammatical breakdown of every word in the ${lname(lang)} sentence.

JSON only, no markdown:
{"sentence":"<${lname(lang)} sentence>","translation":"<English ${lang==="en"?"rephrasing":"translation"}>","breakdown":[{"word":"<word from the ${lname(lang)} sentence>","role":"<grammar explanation>"}]}`);
}

async function apiCorrect(text, lang) {
  return claude(`You are a skilled, encouraging ${lname(lang)} language teacher and editor.
The writer wrote: "${text}"
Correct all grammar, spelling, syntax and vocabulary mistakes so the sentence reads naturally for a fluent ${lname(lang)} speaker. List every mistake found with an explanation. If the sentence is already correct, say so.
Provide a full grammatical breakdown of every word in the corrected sentence.

JSON only, no markdown:
{"corrected":"<corrected sentence>","is_perfect":<true|false>,"mistakes":[{"original":"<incorrect word or phrase>","correction":"<corrected version>","explanation":"<clear explanation of the rule or mistake>"}],"breakdown":[{"word":"<word from corrected sentence>","role":"<grammar explanation>"}]}`);
}

// ══════════════════════════════════════════════════════════════════════════
// TRANSLATE PANEL
// ══════════════════════════════════════════════════════════════════════════
function TranslatePanel({ t }) {
  const [fromLang, setFromLang] = useState("en");
  const [toLang, setToLang]     = useState("de");
  const [cefr, setCefr]         = useState("A1");
  const [input, setInput]       = useState("");
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const prevCefr = useRef(cefr);
  const lastInput = useRef("");

  useEffect(() => {
    if (result && prevCefr.current !== cefr) {
      prevCefr.current = cefr;
      run(lastInput.current);
    }
  }, [cefr]);

  function swap() { setFromLang(toLang); setToLang(fromLang); setResult(null); }

  async function run(word) {
    const w = (word || input).trim();
    if (!w) return;
    lastInput.current = w;
    setLoading(true); setError(""); setResult(null);
    try { setResult(await apiTranslate(w, fromLang, toLang, cefr)); }
    catch { setError("Translation failed. Please try again."); }
    finally { setLoading(false); }
  }

  return (
    <div className="panel">
      <div className="card">
        <div className="section-label">Translate</div>
        <div className="service-bar" style={{marginBottom:14}}>
          <select className="lang-sel" value={fromLang} onChange={e => { setFromLang(e.target.value); setResult(null); }}>
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
          </select>
          <button className="swap" onClick={swap} title="Swap languages">⇄</button>
          <select className="lang-sel" value={toLang} onChange={e => { setToLang(e.target.value); setResult(null); }}>
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
          </select>
        </div>
        <input className="main-input" value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key==="Enter" && run()}
          placeholder={`Type in ${lname(fromLang)}…`} />
        <button className="go-btn" onClick={() => run()} disabled={loading || !input.trim()}>
          {loading ? "Translating…" : "Translate"}
        </button>
      </div>

      {error && <p className="error-msg">{error}</p>}
      {loading && <Loading />}

      {result && !loading && (
        <div className="card" style={{animation:"panelIn .3s ease"}}>
          <ArticlePills active={result.article} t={t} />
          <div className="result-word">
            {result.article && result.article!=="none" ? result.article+" " : ""}{result.translation}
          </div>
          <div className="result-meta">{result.type} · {lname(toLang)}</div>
          <hr className="div" />
          <div className="row-between">
            <div className="section-label">Example sentence</div>
            <CefrDropdown cefr={cefr} setCefr={setCefr} t={t} align="right" />
          </div>
          <div className="ex-sentence">{result.example_to}</div>
          <div className="ex-trans">{result.example_from}</div>
          <Breakdown items={result.breakdown} label={`${lname(toLang)} breakdown`} />
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// WORD OF THE DAY PANEL
// ══════════════════════════════════════════════════════════════════════════
function WodPanel({ t, lang, setLang }) {
  const [cefr, setCefr] = useState("A1");
  const [wod, setWod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true); setError(""); setWod(null);
    try { setWod(await apiWod(lang, cefr)); }
    catch { setError("Failed to load. Try again."); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [lang, cefr]);

  return (
    <div className="panel">
      <div className="service-bar">
        <select className="lang-sel" value={lang} onChange={e => setLang(e.target.value)}>
          {LANGS.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
        </select>
        <CefrDropdown cefr={cefr} setCefr={setCefr} t={t} align="right" />
      </div>

      {loading && <Loading />}
      {error && !loading && (
        <div>
          <p className="error-msg">{error}</p>
          <button className="go-btn" onClick={load}>Retry</button>
        </div>
      )}

      {wod && !loading && !error && (
        <>
          <div className="card">
            <div className="section-label">Word of the Day · {lname(lang)}</div>
            <ArticlePills active={wod.article} t={t} />
            <div className="wod-word">{wod.article && wod.article!=="none" ? wod.article+" " : ""}{wod.word}</div>
            <div className="result-meta">{wod.type}</div>
            <div style={{fontSize:18,color:t.caramel,fontWeight:500,marginBottom:6,lineHeight:1.5}}>{wod.meaning}</div>
            {wod.cultural_note && <div className="cultural">{wod.cultural_note}</div>}
            <hr className="div" />
            <div className="section-label">Example · {cefr}</div>
            <div className="ex-sentence">{wod.example}</div>
            <Breakdown items={wod.breakdown} label={`${lname(lang)} breakdown`} />
          </div>
          <button className="go-btn" onClick={load}>New word →</button>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// FLASHCARDS PANEL
// ══════════════════════════════════════════════════════════════════════════
function FlashPanel({ t, lang, setLang }) {
  const [cefr, setCefr] = useState("A1");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [flipped, setFlipped] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true); setError(""); setCard(null); setFlipped(false);
    try { setCard(await apiFlash(lang, cefr)); }
    catch { setError("Failed to generate card."); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [lang, cefr]);

  return (
    <div className="panel">
      <div className="service-bar">
        <select className="lang-sel" value={lang} onChange={e => setLang(e.target.value)}>
          {LANGS.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
        </select>
        <CefrDropdown cefr={cefr} setCefr={setCefr} t={t} align="right" />
      </div>

      {loading && <Loading />}
      {error && !loading && (
        <div>
          <p className="error-msg">{error}</p>
          <button className="go-btn" onClick={load}>Retry</button>
        </div>
      )}

      {card && !loading && !error && (
        <>
          <div className="flip-outer" onClick={() => setFlipped(f => !f)}>
            <div className={`flip-inner${flipped?" flipped":""}`}>
              <div className="flip-face">
                <div style={{fontSize:10,color:t.muted,letterSpacing:2,marginBottom:14,textTransform:"uppercase"}}>{lname(lang)} · {cefr}</div>
                <div style={{fontSize:16,color:t.cream,textAlign:"center",lineHeight:1.7,fontStyle:"italic"}}>{card.sentence}</div>
                <div className="flip-hint">Tap to reveal</div>
              </div>
              <div className="flip-face flip-back">
                <div style={{fontSize:10,color:t.muted,letterSpacing:2,marginBottom:14,textTransform:"uppercase"}}>English</div>
                <div style={{fontSize:16,color:t.beige,textAlign:"center",lineHeight:1.7,fontWeight:500}}>{card.translation}</div>
                <div className="flip-hint">Tap to flip back</div>
              </div>
            </div>
          </div>
          <Breakdown items={card.breakdown} label={`${lname(lang)} breakdown`} />
          <button className="go-btn" style={{marginTop:12}} onClick={load}>Next card →</button>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// PRACTICE PANEL
// ══════════════════════════════════════════════════════════════════════════
function PracticePanel({ t, history, setHistory, persistHistory, lang, setLang }) {
  const [text, setText] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);

  async function run() {
    if (!text.trim()) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const r = await apiCorrect(text.trim(), lang);
      setResult(r);
      const entry = {
        id: Date.now(),
        original: text.trim(),
        corrected: r.corrected,
        is_perfect: r.is_perfect,
        mistakes: r.mistakes || [],
        breakdown: r.breakdown || [],
        lang,
        date: new Date().toLocaleDateString(),
      };
      const updated = [entry, ...history].slice(0, 100);
      setHistory(updated);
      persistHistory(updated);
    } catch { setError("Analysis failed. Please try again."); }
    finally { setLoading(false); }
  }

  async function clearHistory() {
    setHistory([]);
    try { await window.storage.delete("expressona_practice_history"); } catch {}
  }

  return (
    <div className="panel">
      {/* Input */}
      <div className="card">
        <div className="section-label">Practice</div>
        <div className="service-bar">
          <select className="lang-sel" value={lang} onChange={e => setLang(e.target.value)}>
            {LANGS.map(l => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
          </select>
        </div>
        <textarea className="notes-ta" value={text}
          onChange={e => setText(e.target.value)}
          placeholder={`Write a sentence in ${lname(lang)}…`} />
        <button className="go-btn" onClick={run} disabled={loading || !text.trim()}>
          {loading ? "Analysing…" : "Check & Correct →"}
        </button>
      </div>

      {error && <p className="error-msg">{error}</p>}
      {loading && <Loading />}

      {/* Result */}
      {result && !loading && (
        <div className="card" style={{animation:"panelIn .3s ease"}}>
          {result.is_perfect ? (
            <div className="perfect-badge">
              <div className="perfect-check">✓</div>
              <div className="perfect-text">Perfect — no mistakes found</div>
              <div style={{fontSize:13,color:t.muted,marginTop:4}}>{result.corrected}</div>
            </div>
          ) : (
            <>
              <div className="section-label">Corrected</div>
              <div style={{fontSize:16,color:t.beige,fontWeight:500,lineHeight:1.65,marginBottom:16}}>{result.corrected}</div>
              <hr className="div" />
              <div className="section-label">Mistakes ({result.mistakes?.length})</div>
              {result.mistakes?.map((m,i) => (
                <div key={i} className="mistake-block">
                  <div className="m-orig">✗ {m.original}</div>
                  <div className="m-fix">✓ {m.correction}</div>
                  <div className="m-exp">{m.explanation}</div>
                </div>
              ))}
            </>
          )}
          <Breakdown items={result.breakdown} label={`${lname(lang)} breakdown`} />
        </div>
      )}

      {/* History */}
      <div style={{marginTop:28}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div className="section-label" style={{margin:0}}>History · {history.length} entries</div>
          {history.length > 0 && (
            <button onClick={clearHistory} style={{fontSize:11,color:t.muted,letterSpacing:1}}>
              Clear all
            </button>
          )}
        </div>
        {history.length === 0 ? (
          <div style={{textAlign:"center",padding:"36px 0",color:t.muted,fontSize:13,lineHeight:1.8}}>
            No entries yet.<br/>Write something above to get started.
          </div>
        ) : history.map((h, i) => (
          <div key={h.id || i} className="history-item" onClick={() => setExpanded(expanded===i ? null : i)}>
            <div className="h-meta">
              <span>{getLang(h.lang)?.flag} {getLang(h.lang)?.label} {h.is_perfect ? "· ✓ Perfect" : `· ${h.mistakes?.length || 0} mistake${h.mistakes?.length===1?"":"s"}`}</span>
              <span>{h.date}</span>
            </div>
            <div className="h-orig">{h.original}</div>
            <div className="h-corrected">{h.corrected}</div>
            <div className={`h-expand${expanded===i?" open":""}`}>
              <hr className="div" style={{marginTop:12}} />
              {h.mistakes?.length > 0 && (
                <>
                  <div className="section-label" style={{marginTop:10}}>Mistakes</div>
                  {h.mistakes.map((m,j) => (
                    <div key={j} className="mistake-block">
                      <div className="m-orig">✗ {m.original}</div>
                      <div className="m-fix">✓ {m.correction}</div>
                      <div className="m-exp">{m.explanation}</div>
                    </div>
                  ))}
                </>
              )}
              {h.breakdown?.length > 0 && (
                <>
                  <div className="section-label" style={{marginTop:12}}>Breakdown</div>
                  <div className="bd-list">
                    {h.breakdown.map((b,j) => (
                      <div key={j} className="bd-item">
                        <span className="bd-word">{b.word}</span>
                        <span className="bd-role">{b.role}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ROOT APP
// ══════════════════════════════════════════════════════════════════════════
export default function Expressona() {
  const [active,   setActive]   = useState("translate");
  const [panelKey, setPanelKey] = useState(0);
  const [dark,     setDark]     = useState(false); // light mode is default
  const [history,  setHistory]  = useState([]);
  const [wodLang,      setWodLangState]      = useState("en");
  const [flashLang,    setFlashLangState]    = useState("en");
  const [practiceLang, setPracticeLangState] = useState("en");
  const T = dark ? DARK : LIGHT;

  // Load Practice history + last-used languages once at root so they survive service switching
  useEffect(() => {
    (async () => {
      try {
        const res = await window.storage.get("expressona_practice_history");
        if (res && res.value) setHistory(JSON.parse(res.value));
      } catch {
        // no saved history yet
      }
      try {
        const res = await window.storage.get("expressona_service_langs");
        if (res && res.value) {
          const langs = JSON.parse(res.value);
          if (langs.wod) setWodLangState(langs.wod);
          if (langs.flash) setFlashLangState(langs.flash);
          if (langs.practice) setPracticeLangState(langs.practice);
        }
      } catch {
        // no saved preferences yet
      }
    })();
  }, []);

  async function persistHistory(updated) {
    try { await window.storage.set("expressona_practice_history", JSON.stringify(updated)); } catch {}
  }

  async function persistServiceLangs(next) {
    try {
      const res = await window.storage.get("expressona_service_langs");
      const current = (res && res.value) ? JSON.parse(res.value) : {};
      await window.storage.set("expressona_service_langs", JSON.stringify({ ...current, ...next }));
    } catch {}
  }

  function setWodLang(code) { setWodLangState(code); persistServiceLangs({ wod: code }); }
  function setFlashLang(code) { setFlashLangState(code); persistServiceLangs({ flash: code }); }
  function setPracticeLang(code) { setPracticeLangState(code); persistServiceLangs({ practice: code }); }

  function selectService(id) {
    if (id !== active) {
      setActive(id);
      setPanelKey(k => k + 1);
    }
  }

  return (
    <>
      <style>{makeCSS(T)}</style>
      <div className="app" style={{background:T.bg,color:T.cream}}>

        {/* HEADER */}
        <header className="hdr">
          <div className="hdr-text">
            <div className="logo">Express<em>ona</em></div>
            <div className="tagline">Language · Translation · Learning</div>
          </div>
          <button
            className="theme-switch"
            onClick={() => setDark(d => !d)}
            title={dark ? "Switch to light mode" : "Switch to dark mode"}
            aria-label="Toggle theme"
          >
            <span className={`switch-knob${dark?" dark":""}`}>
              {dark ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" fill={T.cream} stroke={T.cream} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="4" fill={T.cream}/>
                  <line x1="12" y1="2" x2="12" y2="4.5" stroke={T.cream} strokeWidth="2" strokeLinecap="round"/>
                  <line x1="12" y1="19.5" x2="12" y2="22" stroke={T.cream} strokeWidth="2" strokeLinecap="round"/>
                  <line x1="2" y1="12" x2="4.5" y2="12" stroke={T.cream} strokeWidth="2" strokeLinecap="round"/>
                  <line x1="19.5" y1="12" x2="22" y2="12" stroke={T.cream} strokeWidth="2" strokeLinecap="round"/>
                  <line x1="4.93" y1="4.93" x2="6.6" y2="6.6" stroke={T.cream} strokeWidth="2" strokeLinecap="round"/>
                  <line x1="17.4" y1="17.4" x2="19.07" y2="19.07" stroke={T.cream} strokeWidth="2" strokeLinecap="round"/>
                  <line x1="4.93" y1="19.07" x2="6.6" y2="17.4" stroke={T.cream} strokeWidth="2" strokeLinecap="round"/>
                  <line x1="17.4" y1="6.6" x2="19.07" y2="4.93" stroke={T.cream} strokeWidth="2" strokeLinecap="round"/>
                </svg>
              )}
            </span>
          </button>
        </header>

        {/* SERVICE SCROLL */}
        <div className="service-scroll-wrap">
          <div className="service-scroll">
            {SERVICES.map(s => (
              <div key={s.id}
                className={`svc-card${active===s.id?" active":""}`}
                onClick={() => selectService(s.id)}>
                <div className="svc-dot" />
                <div className="svc-icon">{s.icon}</div>
                <div className="svc-label">{s.label}</div>
                <div className="svc-desc">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* PANEL */}
        <div className="panel-wrap">
          {active === "translate" && <TranslatePanel key={panelKey} t={T} />}
          {active === "wod"       && <WodPanel       key={panelKey} t={T} lang={wodLang} setLang={setWodLang} />}
          {active === "flash"     && <FlashPanel     key={panelKey} t={T} lang={flashLang} setLang={setFlashLang} />}
          {active === "practice"  && <PracticePanel  key={panelKey} t={T} history={history} setHistory={setHistory} persistHistory={persistHistory} lang={practiceLang} setLang={setPracticeLang} />}
        </div>

        {/* SUPPORT */}
        <a href="https://ko-fi.com/soufian3" target="_blank" rel="noopener noreferrer" className="support">
          ☕ Support Expressona on Ko-fi
        </a>

      </div>
    </>
  );
}
